// @flow
import window from 'window';
import keycode from 'keycode';
import _ from 'lodash';
import ko from 'rgs-knockout';

import type {
	ISelectStrategy,
	SelectDefaultItem,
} from 'rgs-nfd-ui-kit/ko-components/select/index.flow';
import type { TItemsResponse } from 'app/rgs/api/request-result';
import { ObservableBusy } from 'observable-busy';
import type { ObservableBusyInterface } from 'observable-busy';
import { chooseWordCaseByNumeral } from 'common-utils';
import type { TTextForms } from 'common-utils';
import type { PureComputed } from 'rgs-knockout';

export type SelectBaseStrategyOptions<TItem:* | SelectDefaultItem> = {
	busy?: ObservableBusyInterface;
	fetchSearch?: ({ search: string, take: number, from: number })=>Promise<TItemsResponse<TItem>>;
	pluralize?: TTextForms,
	isDisabled?: PureComputed<boolean>,
};

export const DEFAULT_SIZE_RESULT: number = 5;
export const DEFAULT_MAX_FOR_GET_ALL: number = 250;
export const DEFAULT_MIN_LEN_SEARCH_WORD: number = 3;

export class SelectBaseStrategy<TItem:* | SelectDefaultItem>
	implements ISelectStrategy<TItem> {

	busy = new ObservableBusy;
	pathToId = 'id';
	pathToName = 'name';
	take = DEFAULT_SIZE_RESULT;
	minLengthSearchWord = DEFAULT_MIN_LEN_SEARCH_WORD;
	currentValue = ko.observable();
	inputValue = ko.observable('');
	isOpen = ko.observable(false);
	withSearch = ko.observable(false);
	withShowLoadAll = ko.observable(false);
	isShowLoadAll = ko.pureComputed(() => {
		const itemLength = this.items().length;
		return this.withShowLoadAll()
			&& itemLength > 0
			&& (itemLength < this.total())
			&& (itemLength < DEFAULT_MAX_FOR_GET_ALL);
	});
	contentLoadAll = ko.pureComputed(() => ('Показать ещё'));

	withLoadMore = ko.observable(false);
	isShowLoadMore = ko.pureComputed(() => {
		const itemLength = this.items().length;
		return this.withLoadMore()
			&& itemLength > 0
			&& (this.items().length < this.total());
	});

	contentLoadMore = ko.pureComputed(() => {
		const total = this.total();
		const count = this.items() ? this.items().length : 0;
		const canGetMore = total - count;

		const diff = canGetMore < this.take ? canGetMore : this.take;
		const unit = chooseWordCaseByNumeral(diff, this._options.pluralize);

		return `Показать ещё ${diff} ${unit}`;
	});

	isShowEmpty = ko.pureComputed(() => this.items().length <= 0);
	contentEmpty = 'Ничего не найдено';
	isHasValue = ko.pureComputed(() => {
		const name = this.getItemName(this.currentValue());
		const id = this.getItemId(this.currentValue());

		return (!_.isNil(name) || !_.isNil(id)) === true;
	});
	withCancel = ko.observable(true);
	isDisabled = ko.pureComputed(() => this._options.isDisabled());
	isVisibleCancel = ko.pureComputed(() => {
		return this.withCancel() && this.isHasValue();
	});

	placeholder = 'Выберите значение';
	placeholderInput = 'Начните вводить';
	items = ko.observableArray();
	total = ko.observable(0);

	placeholderText = ko.pureComputed(() => {
		if (!this.isHasValue()) {
			return this.placeholder;
		}

		return this.getCurrentName();
	});

	_options: $NonMaybeType<SelectBaseStrategyOptions<TItem>>;

	constructor (options?: SelectBaseStrategyOptions<TItem>) {
		this._options = _.defaults(options, {
			busy: new ObservableBusy,
			fetchSearch: async () => {
				throw new Error('Не передан параметр fetchSearch!!!');
			},
			pluralize: {
				nominative: 'шт',
				genitive: 'шт',
				genitive_plural: 'шт',
			},
			isDisabled: ko.pureComputed(() => false), // ko.constant
		});

		this.busy = this._options.busy;
	}

	getItemName (item: TItem): string | number {
		return _.get(item, this.pathToName);
	}

	getItemId (item: TItem): string | number {
		return _.get(item, this.pathToId);
	}

	getCurrentName (): string | number {
		return this.getItemName(this.currentValue());
	}

	getCurrentId (): string | number {
		return this.getItemId(this.currentValue());
	}

	onClickCancel (): void {
		this.currentValue(null);
	}

	onClickLayout () {
		if (!this.isDisabled()) {
			this.isOpen() ? this._close() : this._open();
		}
	}

	_open (): void {
		this.isOpen(true);
		this._mountEventsForClose();
	}

	_close (): void {
		this.isOpen(false);
		this._unMountEventsForClose();
	}

	lightClear (): void {
		this.currentValue(null);
		this.inputValue('');
	}

	clear (): void {
		this.currentValue(null);
		this.inputValue('');
		this.items([]);
	}

	setCurrentValue (item: string): void {
		this.currentValue(item);
	}

	async onClickItem (item: TItem): void {
		this.currentValue(item);
		this._close();
	}

	setElement (element: Element): void {
		this.element = element;
	}

	async onClickLoadAll () {
		this.busy.add(this._fetch(this.inputValue(), DEFAULT_MAX_FOR_GET_ALL));
	}

	async onClickLoadMore () {
		const itemsCount = this.items().length;
		const from = itemsCount || 0;

		this.busy.add(this._fetch(this.inputValue(), this.take, from));
	}

	async onChangeInput (
		oldVM: ISelectStrategy<TItem>,
		event: Event,
	) {
		const search = event.target.value;

		if (search.length >= this.minLengthSearchWord) {
			this.busy.add(this._fetch(search, this.take));
		}
	}

	async refresh () {
		const search = this.inputValue();

		if (search.length >= this.minLengthSearchWord) {
			this.busy.add(this._fetch(search, this.take));
		}
	}

	async _fetch (search: string, take: number = this.take, from: number = 0) {
		const { Items, Total } = await this._options.fetchSearch({ search, take, from });

		if (from > 0) {
			const oldItems = this.items() || [];
			oldItems.push(...Items);
			this.items(oldItems);
		} else {
			this.items(Items);
		}
		this.total(Total);
	}

	_mountEventsForClose (): void {
		window.addEventListener('click', this._onHideIfOuterClick, true);
		window.addEventListener('keydown', this._handleKeyDown);
	}

	_unMountEventsForClose (): void {
		window.removeEventListener('click', this._onHideIfOuterClick);
		window.removeEventListener('keydown', this._handleKeyDown);
	}

	_handleKeyDown = (e: Event) => {
		if (e.which === keycode('escape')) {
			this._close();
		}

		return true;
	}

	_onHideIfOuterClick = ({ target }: { target: Node }): void => {
		if (document.body && !document.body.contains(this.element)) {// eslint-disable-line no-undef
			ko.cleanNode(this.element);
			return;
		}

		if (this.isOpen.peek() && !this.element.contains(target)) {
			this._close();
		}
	}
}
